import React from 'react';

export type ComplexityMode = 1 | 3 | 5 | 'words';

interface ComplexityToggleProps {
  value: ComplexityMode;
  onChange: (mode: ComplexityMode) => void;
  className?: string;
  disabled?: boolean;
}

export const ComplexityToggle: React.FC<ComplexityToggleProps> = ({
  value,
  onChange,
  className = '',
  disabled = false
}) => {
  const handleClick = () => {
    if (disabled) return;

    const modes: ComplexityMode[] = [1, 3, 5, 'words'];
    const currentIndex = modes.indexOf(value);
    const nextIndex = (currentIndex + 1) % modes.length;
    onChange(modes[nextIndex]);
  };

  const getTitle = () => {
    switch (value) {
      case 1:
        return 'Single Letters';
      case 3:
        return '1-3 Letter Combinations';
      case 5:
        return '1-5 Letter Combinations';
      case 'words':
        return 'Real Words';
      default:
        return '';
    }
  };

  const getDisplayText = () => {
    switch (value) {
      case 1:
        return '1';
      case 3:
        return '1-3';
      case 5:
        return '1-5';
      case 'words':
        return 'W';
      default:
        return '';
    }
  };

  return (
    <button
      onClick={handleClick}
      title={disabled ? 'Complexity is fixed' : getTitle()}
      className={`complexity-toggle ${disabled ? 'disabled' : ''} ${className}`}
      disabled={disabled}
    >
      <span className="complexity-text">{getDisplayText()}</span>

      <style>{`
        .complexity-toggle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid white;
          background: rgba(17, 24, 39, 0.8);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-out;
          backdrop-filter: blur(10px);
          font-weight: 600;
          font-size: 0.875rem;
        }

        .complexity-toggle:hover {
          transform: scale(1.05);
          background: rgba(59, 130, 246, 0.3);
          border-color: #3b82f6;
        }

        .complexity-toggle:active {
          transform: scale(0.95);
        }

        .complexity-toggle.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(75, 85, 99, 0.3);
          border-color: rgba(75, 85, 99, 0.5);
        }

        .complexity-toggle.disabled:hover {
          transform: none;
          background: rgba(75, 85, 99, 0.3);
          border-color: rgba(75, 85, 99, 0.5);
        }

        .complexity-text {
          font-family: 'Courier New', monospace;
          font-size: 0.75rem;
          font-weight: 700;
          line-height: 1;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .complexity-toggle {
            width: 36px;
            height: 36px;
            border-width: 2px;
          }
          
          .complexity-text {
            font-size: 0.625rem;
          }
        }
      `}</style>
    </button>
  );
};

export default ComplexityToggle;
